// lib/sse-client.ts
export interface SSEEvent {
  event: string;
  data: string;
}

export interface SSERequest {
  url: string;
  body: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

/**
 * Cliente SSE sobre fetch + ReadableStream.
 * Yields {event, data} por cada bloque "event:...\ndata:...\n\n".
 * Throws si response.ok es false (antes de abrir el reader).
 * Si la señal `signal` aborta, cierra el reader y termina el iterator.
 */
export async function* openSSE(req: SSERequest): AsyncIterable<SSEEvent> {
  const response = await fetch(req.url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...req.headers,
    },
    body: JSON.stringify(req.body),
    signal: req.signal,
  });

  if (!response.ok || !response.body) {
    // Intentar extraer el cuerpo del error (varias rutas devuelven JSON
    // con un campo `error` cuando algo falla antes del stream).
    let detail = "";
    try {
      const text = await response.text();
      try {
        const parsed = JSON.parse(text);
        if (parsed && typeof parsed.error === "string") {
          detail = ` — ${parsed.error}`;
        } else {
          detail = text ? ` — ${text.slice(0, 200)}` : "";
        }
      } catch {
        detail = text ? ` — ${text.slice(0, 200)}` : "";
      }
    } catch {
      /* ignore */
    }
    throw new Error(`SSE open failed: ${response.status}${detail}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Parsea bloques separados por "\n\n"
      let sepIndex: number;
      while ((sepIndex = buffer.indexOf("\n\n")) !== -1) {
        const block = buffer.slice(0, sepIndex);
        buffer = buffer.slice(sepIndex + 2);

        const parsed = parseSSEBlock(block);
        if (parsed) yield parsed;
      }
    }

    // Procesa remanente si lo hay
    if (buffer.trim()) {
      const parsed = parseSSEBlock(buffer);
      if (parsed) yield parsed;
    }
  } finally {
    reader.releaseLock();
  }
}

function parseSSEBlock(block: string): SSEEvent | null {
  let event = "message";
  const dataLines: string[] = [];

  for (const line of block.split("\n")) {
    if (!line || line.startsWith(":")) continue;
    const colon = line.indexOf(":");
    if (colon === -1) continue;
    const field = line.slice(0, colon);
    let value = line.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);

    if (field === "event") event = value;
    else if (field === "data") dataLines.push(value);
  }

  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join("\n") };
}
